import { useEffect, useState } from "react";
import type { ContentType, ModelRoute } from "../lib/types.js";
import { api } from "../lib/api.js";

const selectCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

function describePricing(route: ModelRoute): string {
  const { pricing } = route;
  switch (pricing.unit) {
    case "subscription":
      return "included";
    case "token":
      return `$${pricing.inputPer1M ?? 0}/$${pricing.outputPer1M ?? 0} per 1M tok`;
    case "image":
      return `~$${pricing.perUnit ?? 0}/image`;
    case "second":
      return `~$${pricing.perUnit ?? 0}/sec`;
    case "char":
      return `~$${pricing.perUnit ?? 0}/1M chars`;
    default:
      return "";
  }
}

/**
 * Route dropdown ordered by `GET /api/recommend` (best-first per the
 * up-rate/chosen-rate/failure-rate score). An empty selection means "let the
 * server pick the recommended route at generate time".
 */
export function RoutePicker({
  contentType,
  value,
  onChange,
  exclude,
  disabled,
}: {
  contentType: ContentType;
  value?: string;
  onChange: (routeId: string) => void;
  exclude?: string[];
  disabled?: boolean;
}) {
  const [routes, setRoutes] = useState<ModelRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const excludeKey = (exclude ?? []).join(",");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ contentType });
    if (excludeKey) params.set("exclude", excludeKey);
    api<{ items: ModelRoute[] }>(`/api/recommend?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setRoutes(res.items);
      })
      .catch(() => {
        if (!cancelled) setRoutes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contentType, excludeKey]);

  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || loading}
      className={selectCls}
    >
      <option value="">Recommended (auto)</option>
      {routes.map((r) => (
        <option key={r.routeId} value={r.routeId}>
          {r.displayName}
          {r.executor === "local" ? " - local" : ""} - {describePricing(r)}
        </option>
      ))}
    </select>
  );
}
